import React from 'react';
import { Heart, Landmark, Building2, Code2, ArrowUpRight } from 'lucide-react';
import { industries } from '../data/mock';
import { useTheme } from '../context/ThemeContext';

const iconMap = { Heart, Landmark, Building2, Code2 };

// Industry images in order: Healthcare IT, Finance & FinTech, Public Sector, Technology & SaaS
const industryImages = [
  'https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/vcn6mir0_image.png',
  'https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/3hl772ef_image.png',
  'https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/1m04piis_image.png',
  'https://customer-assets.emergentagent.com/job_67218e68-4bb7-4a39-b578-c9fa413502c7/artifacts/n2np5pse_image.png'
];

const IndustriesSection = () => {
  const { theme } = useTheme();

  return (
    <section id="industries" className={`relative py-16 sm:py-20 lg:py-24 transition-colors duration-300 ${
      theme === 'dark' ? 'bg-black' : 'bg-gray-50'
    }`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-12 lg:mb-14">
          <div className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 mb-4 sm:mb-6 border ${
            theme === 'dark' ? 'border-white/20 bg-white/5' : 'border-gray-200 bg-white'
          }`}>
            <span className={`text-xs sm:text-sm font-medium tracking-wide uppercase ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>Industries</span>
          </div>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Industries We Serve
          </h2>
          <p className={`text-base sm:text-lg max-w-[600px] mx-auto ${
            theme === 'dark' ? 'text-white/60' : 'text-gray-600'
          }`}>
            Specialized IT talent across diverse sectors with deep industry expertise.
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {industries.map((industry, index) => {
            const Icon = iconMap[industry.icon];
            return (
              <div
                key={industry.id}
                className={`group relative transition-all duration-300 overflow-hidden ${
                  theme === 'dark' 
                    ? 'bg-[#0a0a0a] border border-white/10 hover:border-[#00FFD1]/40' 
                    : 'bg-white border border-gray-200 hover:border-[#00997A]/40 hover:shadow-lg'
                }`}
              >
                {/* Industry Image */}
                <div className="relative h-32 sm:h-40 lg:h-48 overflow-hidden">
                  <img 
                    src={industryImages[index]} 
                    alt={industry.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 ${
                    theme === 'dark' 
                      ? 'bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent' 
                      : 'bg-gradient-to-t from-white via-transparent to-transparent'
                  }`} />
                </div>

                {/* Content */}
                <div className="relative p-4 sm:p-5 lg:p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 sm:p-2.5 transition-colors ${
                      theme === 'dark' 
                        ? 'bg-[#00FFD1]/10 group-hover:bg-[#00FFD1]/20' 
                        : 'bg-[#00997A]/10 group-hover:bg-[#00997A]/20'
                    }`}>
                      <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        theme === 'dark' ? 'text-[#00FFD1]' : 'text-[#00997A]'
                      }`} />
                    </div>
                    <ArrowUpRight 
                      className={`w-4 h-4 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ${
                        theme === 'dark' ? 'text-white/20 group-hover:text-[#00FFD1]' : 'text-gray-300 group-hover:text-[#00997A]'
                      }`}
                    />
                  </div>
                  
                  <h3 className={`text-sm sm:text-base lg:text-lg font-semibold mb-1.5 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {industry.title}
                  </h3>
                  
                  <p className={`text-xs sm:text-sm leading-relaxed ${
                    theme === 'dark' ? 'text-white/60' : 'text-gray-600'
                  }`}>
                    {industry.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
